import { useState, useEffect, useRef } from 'react';
import { formatPhone } from '../components/formatPhone';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { auth } from '../firebase';
import Sidebar from './Sidebar';
import Navbar from './Navbar';
import {
  FaWrench, FaArrowLeft, FaDollarSign, FaBuilding, FaUser,
  FaCalendarAlt, FaClock, FaCheckCircle, FaExclamationTriangle,
  FaPhone, FaTimes, FaTrash, FaTools, FaPlus, FaEdit, FaImages, FaExpand, FaKey,
} from 'react-icons/fa';
import './Sidebar.css';
import './Navbar.css';
import './WorkOrders.css';
import './WorkOrderDetails.css';
import './Home.css';
import LoadingScreen from './LoadingScreen';
import { showToast } from '../components/toast';

const BASE_URL = 'https://api.rside.app';

function WorkOrderDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [workOrder, setWorkOrder] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [initials, setInitials] = useState('');
  const [showExpenseModal, setShowExpenseModal] = useState(false);
  const [allExpenses, setAllExpenses] = useState([]);
  const [selectedExpenseId, setSelectedExpenseId] = useState('');
  const [statusUpdating, setStatusUpdating] = useState(false);
  const [vendors, setVendors] = useState([]);
  const [showAddVendorModal, setShowAddVendorModal] = useState(false);
  const [selectedVendorId, setSelectedVendorId] = useState('');
  const [assignNotes, setAssignNotes] = useState('');
  const [assignSubmitting, setAssignSubmitting] = useState(false);
  const [woAssignedVendors, setWoAssignedVendors] = useState([]);
  const [woPhotos, setWoPhotos] = useState([]);
  const [photosLoading, setPhotosLoading] = useState(false);
  const [lightboxPhoto, setLightboxPhoto] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editForm, setEditForm] = useState({ title: '', priority: 'Medium', details: '', dueDate: '', category: '', entryRequest: false, entryDate: '' });
  const [editPhotos, setEditPhotos] = useState([]);
  const editPhotoInputRef = useRef(null);
  const [pmEmail, setPmEmail] = useState('');
  const [pmFullName, setPmFullName] = useState('');
  const [propertiesList, setPropertiesList] = useState([]);
  const [tenantInfo, setTenantInfo] = useState(null);

  useEffect(() => {
    const unsub = auth.onAuthStateChanged(async (user) => {
      if (!user) return navigate('/login');
      try {
        // Get PM name for initials
        setPmEmail(user.email);
        const nameRes = await fetch(`${BASE_URL}/PropertyManagerName?email=${encodeURIComponent(user.email)}`);
        const nameData = await nameRes.json();
        const parts = nameData.Full_Name?.split(' ') || [];
        setInitials((parts[0]?.[0] || '') + (parts.at(-1)?.[0] || ''));
        setPmFullName(nameData.Full_Name || '');

        // Fetch properties so we can resolve door_id for editing and tenant lookup
        let propsList = [];
        const propsRes = await fetch(`${BASE_URL}/GetAllProperties?full_name=${encodeURIComponent(nameData.Full_Name || '')}&Email=${encodeURIComponent(user.email)}`);
        if (propsRes.ok) {
          const propsData = await propsRes.json();
          propsList = propsData.Properties || [];
          setPropertiesList(propsList);
        }

        // Always fetch fresh details from GetWorkOrderDetails for entry_request data
        let baseWO = null;
        const detailRes = await fetch(`${BASE_URL}/GetWorkOrderDetails?work_order_id=${encodeURIComponent(id)}`);
        if (detailRes.ok) {
          const d = await detailRes.json();
          const found = d.work_order || d;
          if (found && (found.id || found.ID)) {
            const p = found.Priority;
            baseWO = {
              ID: found.id || found.ID,
              Title: found.Title || 'Untitled',
              Priority: p === 3 || p === '3' ? 'Urgent' : p === 1 || p === '1' ? 'Low' : 'Medium',
              Description: found.Details || found.Description || '',
              Unit_Number: found.Unit_Number || '',
              Property_Name: found.Property_Name || '',
              Date_Submitted: found.Create_Date || found.Date_Submitted || '',
              Due_Date: found.Due_Date || '',
              Status: found.Stat || found.Status || 'Open',
              Door_id: found.door_id || found.Door_id || found.Door_ID || '',
              Category: found.Category || found.category || '',
              Entry_Request_Date: found.entry_request_date || found.Entry_Request_Date || '',
              Entry_Request_Status: found.entry_request_status || found.Entry_Request_Status || '',
              Close_Requested: !!(found.RequestToDelete || found.close_requested || found.Close_Requested || found.request_to_delete),
            };
          }
        }

        // Fallback to navigation state then list endpoint
        if (!baseWO) {
          baseWO = location.state?.workOrder || null;
          if (!baseWO) {
            const res = await fetch(`${BASE_URL}/GetWorkOrdersByPM?pm_email=${encodeURIComponent(user.email)}`);
            if (res.ok) {
              const data = await res.json();
              const found = (data.work_orders || []).find(w => String(w.id) === String(id));
              if (found) {
                const p = found.Priority;
                baseWO = {
                  ID: found.id,
                  Title: found.Title || 'Untitled',
                  Priority: p === 3 || p === '3' ? 'Urgent' : p === 1 || p === '1' ? 'Low' : 'Medium',
                  Description: found.Details || '',
                  Unit_Number: found.Unit_Number || '',
                  Property_Name: found.Property_Name || '',
                  Date_Submitted: found.Create_Date || '',
                  Due_Date: found.Due_Date || '',
                  Status: found.Stat || 'Open',
                  Door_id: found.door_id || found.Door_id || found.Door_ID || '',
                  Category: found.Category || found.category || '',
                  Entry_Request_Date: found.entry_request_date || '',
                  Entry_Request_Status: found.entry_request_status || '',
                  Close_Requested: !!(found.RequestToDelete || found.close_requested || found.Close_Requested || found.request_to_delete),
                };
              }
            }
          }
        }

        if (!baseWO) { setNotFound(true); return; }
        setWorkOrder(baseWO);

        // Fetch tenant info — use Door_id from work order or resolve from properties list
        const resolvedDoorId = baseWO.Door_id || (() => {
          const match = propsList.find(p =>
            p.Property_Name === baseWO.Property_Name &&
            String(p.Unit_Number || '') === String(baseWO.Unit_Number || '')
          ) || propsList.find(p => p.Property_Name === baseWO.Property_Name);
          return match?.Door_id || '';
        })();
        if (resolvedDoorId) {
          fetch(`${BASE_URL}/Lease&TenantsByDoor?door_id=${encodeURIComponent(resolvedDoorId)}`)
            .then(r => r.ok ? r.json() : null)
            .then(d => {
              if (!d) return;
              const t = d?.tenants?.[0] || d?.tenant || null;
              if (t) setTenantInfo({ name: t.Full_Name || t.full_name || '', phone: t.Phone || t.phone_number || t.Phone_Number || '' });
            })
            .catch(() => {});
        }

        // Fetch timeline + expenses + PM expense list + vendors in parallel
        const emailToName = { [user.email]: nameData.Full_Name || '' };
        const [, woExps, allExps] = await Promise.all([
          loadTimeline(id, emailToName),
          loadExpenses(id),
          loadAllExpenses(user.email, resolvedDoorId),
          loadVendors(user.email),
          loadWoAssignedVendors(id),
          loadWoPhotos(id),
        ]);
        // Enrich WO expenses: cross-ref ListExpense by id, else fall back to work order category
        if (woExps?.length > 0) {
          setExpenses(woExps.map(exp => {
            if (exp.category) return exp;
            const match = allExps?.find(ae => ae.expense_id === exp.id);
            const category = match?.category || baseWO?.Category || '';
            return { ...exp, category };
          }));
        }
      } catch (err) {
        console.error(err);
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    });
    return unsub;
  }, [navigate, id]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadTimeline = async (woId, emailToName = {}) => {
    try {
      const res = await fetch(`${BASE_URL}/GetWorkOrderTimeline?work_order_id=${encodeURIComponent(woId)}`);
      if (res.ok) {
        const data = await res.json();
        const raw = data.timeline || data.Timeline || (Array.isArray(data) ? data : []);
        setTimeline(raw.map(e => ({
          ...e,
          _author: emailToName[e.Updated_By] || emailToName[e.updated_by] || e.Updated_By_Name || e.Full_Name || e.Name || e.Updated_By || e.updated_by || '',
        })));
      }
    } catch (err) {
      console.error('Timeline fetch failed:', err);
    }
  };

  const loadExpenses = async (woId) => {
    try {
      const res = await fetch(`${BASE_URL}/GetExpensesByWorkOrder?work_order_id=${encodeURIComponent(woId)}`);
      if (res.ok) {
        const data = await res.json();
        const raw = data.expenses || data.Expenses || (Array.isArray(data) ? data : []);
        const mapped = raw.map(e => ({
          id:       String(e.id || e.ID || e.Expense_ID || e.expense_id || ''),
          amount:   Math.abs(Number(e.Amount || e.amount || 0)),
          title:    e.Title || e.Vendor || e.vendor || e.title || '',
          category: e.Category || e.category || e.Type || e.type || '',
          date:     e.Paydate || e.paydateString || e.paydate || e.PayDate || e.Pay_Date || e.Date || e.date || '',
          notes:    e.Notes || e.notes || '',
        }));
        setExpenses(mapped);
        return mapped;
      }
    } catch (err) {
      console.error('Expenses fetch failed:', err);
    }
    return [];
  };

  const loadAllExpenses = async (email, doorId) => {
    try {
      if (doorId) {
        const res = await fetch(`${BASE_URL}/ExpensesByDoor?door_id=${encodeURIComponent(doorId)}`);
        if (res.ok) {
          const data = await res.json();
          const raw = Array.isArray(data) ? data
            : Array.isArray(data.expenses) ? data.expenses
            : Array.isArray(data.Expenses) ? data.Expenses : [];
          const mapped = raw.map(e => ({
            expense_id: String(e.expense_id || e.id || e.ID || ''),
            amount:     Math.abs(Number(e.Amount || e.amount || 0)),
            category:   e.Category || e.category || '',
            vendor:     e.title || e.Title || e.Vendor || '',
            date:       e.paydateString || e.paydate || e.PayDate || e.Paydate || '',
            notes:      e.Notes || e.notes || '',
            property:   e.Property_Name || e.property_name || '',
          }));
          setAllExpenses(mapped);
          return mapped;
        }
      }
      const res = await fetch(`${BASE_URL}/ListExpense?pm_email=${encodeURIComponent(email)}`);
      if (res.ok) {
        const data = await res.json();
        const raw = data.expenses || data.Expenses || (Array.isArray(data) ? data : []);
        const mapped = raw.map(e => ({
          expense_id: String(e.expense_id || e.ID || e.Expense_ID || ''),
          amount:     Math.abs(Number(e.Amount || 0)),
          category:   e.Category || '',
          vendor:     e.Title || e.Vendor || '',
          date:       e.Paydate || e.Date || '',
          notes:      e.Notes || '',
          property:   e.Property_Name || '',
        }));
        setAllExpenses(mapped);
        return mapped;
      }
    } catch (err) {
      console.error('All expenses fetch failed:', err);
    }
    return [];
  };

  const loadVendors = async (email) => {
    try {
      const res = await fetch(`${BASE_URL}/GetServiceProsByPM?pm_email=${encodeURIComponent(email)}`);
      if (res.ok) {
        const data = await res.json();
        setVendors(data.service_pros || data.vendors || data.Vendors || (Array.isArray(data) ? data : []));
      }
    } catch (err) {
      console.error('Vendors fetch failed:', err);
    }
  };

  const loadWoAssignedVendors = async (woId) => {
    try {
      const res = await fetch(`${BASE_URL}/GetWorkOrderServicePro?work_order_id=${encodeURIComponent(woId)}`);
      if (res.ok) {
        const data = await res.json();
        setWoAssignedVendors(data.service_pros || []);
      }
    } catch (err) {
      console.error('Assigned vendors fetch failed:', err);
    }
  };

  const loadWoPhotos = async (woId) => {
    setPhotosLoading(true);
    try {
      const res = await fetch(`${BASE_URL}/GetWorkOrderImages?work_order_id=${encodeURIComponent(woId)}`);
      if (!res.ok) return;
      const data = await res.json();
      const photos = data.photos || [];
      const loaded = await Promise.all(photos.map(async (p) => {
        try {
          const imgRes = await fetch(`${BASE_URL}/GetWorkOrderImageFile?photo_id=${encodeURIComponent(p.photo_id)}`);
          if (!imgRes.ok) return { ...p, blobUrl: p.image_url || null };
          const blob = await imgRes.blob();
          return { ...p, blobUrl: URL.createObjectURL(blob) };
        } catch {
          return { ...p, blobUrl: p.image_url || null };
        }
      }));
      setWoPhotos(loaded.filter(p => p.blobUrl));
    } catch (err) {
      console.error('Photos fetch failed:', err);
    } finally {
      setPhotosLoading(false);
    }
  };

  const handleAssignVendor = async () => {
    if (!selectedVendorId) return;
    setAssignSubmitting(true);
    try {
      const res = await fetch(
        `${BASE_URL}/AddWorkOrderServicePro?work_order_id=${encodeURIComponent(id)}&service_pro_id=${encodeURIComponent(selectedVendorId)}&notes=${encodeURIComponent(assignNotes)}`,
        { method: 'POST' }
      );
      if (res.ok) {
        const user = auth.currentUser;
        const v = vendors.find(v => String(v.id || v.ID) === String(selectedVendorId));
        const vName = v?.Full_Name || v?.full_name || 'Vendor';
        await fetch(
          `${BASE_URL}/AddWorkOrderTimeline?title=${encodeURIComponent(`Vendor assigned: ${vName}`)}&updated_by=${encodeURIComponent(pmFullName || user?.email || 'System')}&work_order_id=${id}`,
          { method: 'POST' }
        );
        await Promise.all([loadWoAssignedVendors(id), loadTimeline(id)]);
        setSelectedVendorId('');
        setAssignNotes('');
        setShowAddVendorModal(false);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setAssignSubmitting(false);
    }
  };

  const handleRemoveAssignedVendor = async (assignmentId) => {
    const user = auth.currentUser;
    const v = woAssignedVendors.find(v => v.Assignment_id === assignmentId);
    const vName = v?.Full_Name || v?.full_name || 'Vendor';
    try {
      await fetch(`${BASE_URL}/DeleteWorkOrderServicePro?assignment_id=${encodeURIComponent(assignmentId)}`, { method: 'DELETE' });
      setWoAssignedVendors(prev => prev.filter(v => v.Assignment_id !== assignmentId));
      await fetch(
        `${BASE_URL}/AddWorkOrderTimeline?title=${encodeURIComponent(`Vendor removed: ${vName}`)}&updated_by=${encodeURIComponent(pmFullName || user?.email || 'System')}&work_order_id=${id}`,
        { method: 'POST' }
      );
      await loadTimeline(id);
    } catch (err) {
      console.error(err);
    }
  };

  const handleDeletePhoto = async (photoId) => {
    const user = auth.currentUser;
    try {
      await fetch(`${BASE_URL}/DeleteWorkOrderImage?photo_id=${encodeURIComponent(photoId)}`, { method: 'DELETE' });
      setWoPhotos(prev => prev.filter(p => p.photo_id !== photoId));
      if (lightboxPhoto?.photo_id === photoId) setLightboxPhoto(null);
      await fetch(
        `${BASE_URL}/AddWorkOrderTimeline?title=${encodeURIComponent('Photo removed')}&updated_by=${encodeURIComponent(pmFullName || user?.email || 'System')}&work_order_id=${id}`,
        { method: 'POST' }
      );
      await loadTimeline(id);
    } catch (err) {
      console.error(err);
    }
  };


  const handleDeleteWorkOrder = async () => {
    try {
      const res = await fetch(
        `${BASE_URL}/DeleteWorkOrder?work_order_id=${encodeURIComponent(id)}`,
        { method: 'DELETE' }
      );
      if (res.ok) navigate('/work-orders');
    } catch (err) {
      console.error(err);
    }
  };

  const openEditModal = () => {
    const existingEntryDate = workOrder.Entry_Request_Date
      ? workOrder.Entry_Request_Date.split('T')[0]
      : '';
    setEditForm({
      title:        workOrder.Title || '',
      priority:     workOrder.Priority || 'Medium',
      details:      workOrder.Description || '',
      dueDate:      workOrder.Due_Date ? workOrder.Due_Date.split('T')[0] : '',
      category:     workOrder.Category || '',
      entryRequest: !!workOrder.Entry_Request_Status,
      entryDate:    existingEntryDate,
    });
    setEditPhotos([]);
    setShowEditModal(true);
  };

  const handleEditWorkOrder = async () => {
    if (!editForm.title.trim()) return;
    if (!editForm.category) { showToast('Please select a category.', 'warning'); return; }
    if (editForm.entryRequest && !editForm.entryDate) {
      showToast('Please select a date for the entry request.', 'warning');
      return;
    }
    try {
      const user = auth.currentUser;
      const priorityValue = editForm.priority === 'Urgent' ? '3' : editForm.priority === 'Low' ? '1' : '2';
      const matchingProp = propertiesList.find(p =>
        String(p.Unit_Number) === String(workOrder.Unit_Number) &&
        p.Property_Name === workOrder.Property_Name
      ) || propertiesList.find(p => p.Property_Name === workOrder.Property_Name);
      const doorId = matchingProp?.Door_id || matchingProp?.door_id || '';
      const body = new FormData();
      body.append('work_order_id', id);
      body.append('title',         editForm.title.trim());
      body.append('priority',      priorityValue);
      body.append('details',       editForm.details.trim());
      body.append('door_id',       doorId);
      body.append('Category',      editForm.category);
      if (editForm.dueDate) body.append('due_date', `${editForm.dueDate}T00:00:00`);
      // Only send entry_request_date for brand-new requests — sending it on an already-responded
      // request causes the API to reset the status back to Pending.
      const prevStatus = workOrder.Entry_Request_Status || '';
      const isResolved = prevStatus === 'Approved' || prevStatus === 'Denied';
      const isNewRequest = editForm.entryRequest && !prevStatus;
      if (isNewRequest && editForm.entryDate) body.append('entry_request_date', `${editForm.entryDate}T00:00:00`);
      editPhotos.forEach((file, i) => body.append('photos', file, `photo_${i}.jpg`));

      const res = await fetch(`${BASE_URL}/EditWorkOrder`, { method: 'PUT', body });
      if (!res.ok) {
        const errBody = await res.text().catch(() => '');
        showToast(`Edit failed (${res.status}): ${errBody || 'no details'}`, 'error');
        return;
      }

      const wasRequested = !!prevStatus;
      const nowRequested = editForm.entryRequest;
      let newEntryStatus = prevStatus;
      let newEntryDate = workOrder.Entry_Request_Date || '';
      if (isNewRequest) {
        newEntryStatus = 'Pending';
        newEntryDate = editForm.entryDate;
        await fetch(
          `${BASE_URL}/AddWorkOrderTimeline?title=${encodeURIComponent('Entry requested by manager')}&updated_by=${encodeURIComponent(pmFullName || user?.email || 'System')}&work_order_id=${id}`,
          { method: 'POST' }
        );
      } else if (!nowRequested && wasRequested && !isResolved) {
        // Only clear if it was Pending — never wipe an Approved/Denied response
        newEntryStatus = '';
        newEntryDate = '';
      }

      setWorkOrder(p => ({
        ...p,
        Title:                editForm.title,
        Priority:             editForm.priority,
        Description:          editForm.details,
        Due_Date:             editForm.dueDate,
        Category:             editForm.category,
        Entry_Request_Date:   newEntryDate,
        Entry_Request_Status: newEntryStatus,
      }));

      // Build specific change log entries
      const changes = [];
      if (editForm.title.trim() !== (workOrder.Title || '').trim()) changes.push(`Title updated to "${editForm.title.trim()}"`);
      if (editForm.priority !== (workOrder.Priority || 'Medium')) changes.push(`Priority changed to ${editForm.priority}`);
      if (editForm.category !== (workOrder.Category || '')) changes.push(`Category changed to ${editForm.category}`);
      if (editForm.dueDate !== ((workOrder.Due_Date || '').split('T')[0])) {
        changes.push(editForm.dueDate ? `Due date set to ${editForm.dueDate}` : 'Due date cleared');
      }
      if (editForm.details.trim() !== (workOrder.Description || '').trim()) changes.push('Description updated');
      if (editPhotos.length > 0) changes.push(`${editPhotos.length} photo${editPhotos.length > 1 ? 's' : ''} added`);

      if (editPhotos.length > 0) await loadWoPhotos(id);

      const logEntries = changes.length > 0 ? changes : ['Work order updated'];
      await Promise.all(logEntries.map(entry =>
        fetch(
          `${BASE_URL}/AddWorkOrderTimeline?title=${encodeURIComponent(entry)}&updated_by=${encodeURIComponent(pmFullName || user?.email || 'System')}&work_order_id=${id}`,
          { method: 'POST' }
        )
      ));
      await loadTimeline(id);
      setEditPhotos([]);
      setShowEditModal(false);
    } catch (err) {
      console.error(err);
    }
  };

  const handleUpdateStatus = async (newStatus) => {
    setStatusUpdating(true);
    try {
      const user = auth.currentUser;
      const res = await fetch(
        `${BASE_URL}/UpdateWorkOrderStatus?work_order_id=${encodeURIComponent(id)}&status=${encodeURIComponent(newStatus)}`,
        { method: 'PUT' }
      );
      if (res.ok) {
        setWorkOrder(p => ({ ...p, Status: newStatus }));
        await fetch(
          `${BASE_URL}/AddWorkOrderTimeline?title=${encodeURIComponent('Status updated to ' + newStatus)}&updated_by=${encodeURIComponent(pmFullName || user?.email || 'System')}&work_order_id=${id}`,
          { method: 'POST' }
        );
        await loadTimeline(id);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setStatusUpdating(false);
    }
  };

  const handleAttachExpense = async () => {
    if (!selectedExpenseId) return;
    try {
      const user = auth.currentUser;
      const picked = allExpenses.find(e => String(e.expense_id) === String(selectedExpenseId));
      const amount = picked ? picked.amount.toFixed(2) : '';
      const res = await fetch(
        `${BASE_URL}/AttachExpenseToWorkOrder?expense_id=${encodeURIComponent(selectedExpenseId)}&work_order_id=${encodeURIComponent(id)}`,
        { method: 'PUT' }
      );
      if (res.ok) {
        // Add timeline entry for the attachment
        await fetch(
          `${BASE_URL}/AddWorkOrderTimeline?title=${encodeURIComponent(`Expense attached${amount ? ` — $${amount}` : ''}`)}&updated_by=${encodeURIComponent(pmFullName || user?.email || 'System')}&work_order_id=${id}`,
          { method: 'POST' }
        );
        // Refresh both expenses and timeline
        await Promise.all([loadExpenses(id), loadTimeline(id)]);
        setShowExpenseModal(false);
        setSelectedExpenseId('');
      }
    } catch (err) {
      console.error(err);
    }
  };

  const fmtDate = (d) => {
    if (!d) return '—';
    const dt = new Date(d);
    return isNaN(dt) ? d : dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const fmtDateTime = (d) => {
    if (!d) return '—';
    // API returns UTC datetimes without 'Z' — append it so the browser converts to local time correctly
    const normalized = typeof d === 'string' && !/[Zz]|[+-]\d{2}:\d{2}/.test(d) ? d + 'Z' : d;
    const dt = new Date(normalized);
    if (isNaN(dt)) return d;
    return dt.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const statusBadgeClass = (s) => {
    if (s === 'Completed')   return 'wod-badge-done';
    if (s === 'In Progress') return 'wod-badge-inprog';
    return 'wod-badge-open';
  };

  const priorityBadgeClass = (p) => {
    if (p === 'Urgent') return 'wod-badge-high';
    if (p === 'Low')  return 'wod-badge-low';
    return 'wod-badge-med';
  };

  if (loading) return <LoadingScreen />;

  if (notFound || !workOrder) return (
    <div className="home-page">
      <Sidebar initials={initials} />
      <div className="main-content">
        <Navbar title="Work Orders" />
        <div className="wod-container">
          <button className="wod-back-btn" onClick={() => navigate('/work-orders')}>
            <FaArrowLeft /> Back to Work Orders
          </button>
          <div className="wod-not-found">
            <div className="wod-nf-icon"><FaWrench /></div>
            <h3>Work Order Not Found</h3>
            <p>We couldn't load this work order. It may have been removed or the ID is invalid.</p>
            <button className="wod-nf-btn" onClick={() => navigate('/work-orders')}>Go Back</button>
          </div>
        </div>
      </div>
    </div>
  );

  const status = workOrder.Status;
  const totalCost = expenses.reduce((sum, e) => sum + (e.amount || 0), 0);

  return (
    <div className="home-page">
      <Sidebar initials={initials} />
      <div className="main-content">
        <Navbar title="Work Orders" />
        <div className="wod-container">

          <button className="wod-back-btn" onClick={() => navigate('/work-orders')}>
            <FaArrowLeft /> Back to Work Orders
          </button>

          {/* Hero card */}
          <div className="wod-hero-card">
            <div className="wod-hero-icon-wrap"><FaWrench /></div>
            <div className="wod-hero-body">
              <h1 className="wod-hero-title">{workOrder.Title}</h1>
              <div className="wod-hero-meta">
                <span className="wod-hero-sub">Submitted {fmtDate(workOrder.Date_Submitted)}</span>
                {workOrder.Due_Date && (
                  <>
                    <span className="wod-hero-meta-sep">·</span>
                    <span className={`wod-hero-sub${new Date(workOrder.Due_Date) < new Date() && workOrder.Status !== 'Completed' ? ' wod-hero-due-late' : ''}`}>
                      Due {fmtDate(workOrder.Due_Date)}
                    </span>
                  </>
                )}
                <span className="wod-hero-meta-sep">·</span>
                <div className="wod-hero-badges">
                  <span className={`wod-badge ${statusBadgeClass(status)}`}>{status}</span>
                  <span className={`wod-badge ${priorityBadgeClass(workOrder.Priority)}`}>{workOrder.Priority} Priority</span>
                  {workOrder.Category && (
                    <span className="wod-badge wod-badge-category">{workOrder.Category}</span>
                  )}
                </div>
              </div>
            </div>
            <div className="wod-hero-btns">
              <button className="wod-hero-action-btn wod-edit-btn" onClick={openEditModal}>
                <FaEdit /> Edit
              </button>
            </div>
          </div>

          {/* Main layout */}
          <div className="wod-body">
            <div className="wod-left">

              {/* Description */}
              <div className="wod-section">
                <h2 className="wod-section-title">Description</h2>
                <p className="wod-description">{workOrder.Description || 'No description provided.'}</p>
              </div>

              {/* Photos */}
              {(photosLoading || woPhotos.length > 0) && (
                <div className="wod-section">
                  <div className="wod-section-hd">
                    <h2 className="wod-section-title" style={{ margin: 0 }}>
                      <FaImages style={{ marginRight: 8, color: '#6b7280', fontSize: 16 }} />
                      Photos
                    </h2>
                    <span className="wod-photo-count">{woPhotos.length} photo{woPhotos.length !== 1 ? 's' : ''}</span>
                  </div>
                  {photosLoading ? (
                    <p className="wod-empty-text">Loading photos…</p>
                  ) : (
                    <div className="wod-photos-grid">
                      {woPhotos.map((photo) => (
                        <div key={photo.photo_id} className="wod-photo-thumb-wrap">
                          <img
                            src={photo.blobUrl}
                            alt="Work order"
                            className="wod-photo-thumb"
                            onClick={() => setLightboxPhoto(photo)}
                          />
                          <button className="wod-photo-expand-btn" onClick={() => setLightboxPhoto(photo)}>
                            <FaExpand />
                          </button>
                          <button className="wod-photo-delete-btn" onClick={() => handleDeletePhoto(photo.photo_id)}>
                            <FaTimes />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Timeline */}
              <div className="wod-section">
                <h2 className="wod-section-title">Timeline</h2>
                {timeline.length === 0 ? (
                  <p className="wod-empty-text">No timeline events yet.</p>
                ) : (
                  <div className="wod-timeline">
                    {[...timeline].reverse().map((event, i, arr) => (
                      <div key={i} className="wod-tl-item">
                        <div className="wod-tl-dot-wrap">
                          <div className={`wod-tl-dot ${i === 0 ? 'wod-tl-dot-first' : ''}`}>
                            {i === 0 ? <FaClock /> : <FaCalendarAlt />}
                          </div>
                          {i < arr.length - 1 && <div className="wod-tl-line" />}
                        </div>
                        <div className="wod-tl-content">
                          <span className="wod-tl-title">
                            {event.Event || event.event || event.Title || event.title || 'Update'}
                          </span>
                          <span className="wod-tl-date">
                            {fmtDateTime(event.Date || event.date || event.Created_At || event.created_at)}
                            {event._author && <> · {event._author}</>}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Attached Expenses */}
              <div className="wod-section">
                <div className="wod-section-hd">
                  <h2 className="wod-section-title" style={{ margin: 0 }}>Attached Expenses</h2>
                  {expenses.length > 0 && (
                    <span className="wod-exp-total">
                      Total: ${totalCost.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                    </span>
                  )}
                </div>
                {expenses.length === 0 ? (
                  <p className="wod-empty-text">No expenses attached yet.</p>
                ) : (
                  <div className="wod-exp-scroll">
                    <table className="fm-table" style={{ tableLayout: 'fixed', width: '100%' }}>
                      <thead>
                        <tr>
                          <th style={{ width: '6%' }}>ID</th>
                          <th style={{ width: '30%' }}>TITLE</th>
                          <th style={{ width: '20%', textAlign: 'left', paddingLeft: 16 }}>AMOUNT</th>
                          <th style={{ width: '22%' }}>CATEGORY</th>
                          <th style={{ width: '22%' }}>DATE</th>
                        </tr>
                      </thead>
                      <tbody>
                        {expenses.map((exp, i) => (
                          <tr key={i}>
                            <td className="fm-id-col">#{i + 1}</td>
                            <td className="fm-title-col">{exp.title || '—'}</td>
                            <td style={{ paddingLeft: 16 }}>
                              <span className="fm-amount-cell" style={{ color: '#dc2626', fontWeight: 700 }}>
                                <span className="fm-dot fm-dot-red" />
                                ${exp.amount.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                              </span>
                            </td>
                            <td>
                              {exp.category
                                ? <span className="wod-exp-cat-badge">{exp.category}</span>
                                : <span className="wod-exp-empty">—</span>}
                            </td>
                            <td>{fmtDate(exp.date)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

            </div>

            {/* Right column */}

            <div className="wod-right">

              {/* Close Request Banner */}
              {workOrder.Close_Requested && (
                <div className="wod-close-request-banner wod-card-full">
                  <div className="wod-close-request-left">
                    <FaExclamationTriangle className="wod-close-request-icon" />
                    <div>
                      <div className="wod-close-request-title">Tenant requested to close</div>
                      <div className="wod-close-request-sub">The tenant has asked to close this work order.</div>
                    </div>
                  </div>
                  <button className="wod-close-delete-btn" onClick={() => setShowDeleteConfirm(true)}>
                    <FaTrash style={{ marginRight: 6 }} /> Delete Work Order
                  </button>
                </div>
              )}

              {/* Tenant Info */}
              <div className="wod-info-card">
                <div className="wod-info-card-hd">
                  <span className="wod-info-icon wod-info-icon-blue"><FaUser /></span>
                  <h3 className="wod-info-card-title">Tenant Information</h3>
                </div>
                {tenantInfo?.name ? (
                  <div className="wod-info-rows">
                    <div className="wod-info-row">
                      <span className="wod-info-lbl">Name</span>
                      <span className="wod-info-val">{tenantInfo.name}</span>
                    </div>
                    {tenantInfo.phone && (
                      <div className="wod-info-row">
                        <span className="wod-info-lbl">Phone</span>
                        <span className="wod-info-val">
                          <FaPhone style={{ fontSize: 11, marginRight: 5, color: '#6b7280' }} />
                          {formatPhone(tenantInfo.phone)}
                        </span>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="wod-vacant-msg">
                    <FaUser className="wod-vacant-icon" />
                    <span>No tenant on record</span>
                  </div>
                )}
              </div>

              {/* Property Info */}
              <div className="wod-info-card">
                <div className="wod-info-card-hd">
                  <span className="wod-info-icon wod-info-icon-green"><FaBuilding /></span>
                  <h3 className="wod-info-card-title">Property Information</h3>
                </div>
                <div className="wod-info-rows-3">
                  <div className="wod-info-row">
                    <span className="wod-info-lbl">Property</span>
                    <span className="wod-info-val">{workOrder.Property_Name || '—'}</span>
                  </div>
                  <div className="wod-info-row">
                    <span className="wod-info-lbl">Unit</span>
                    <span className="wod-info-val">{workOrder.Unit_Number || '—'}</span>
                  </div>
                  <div className="wod-info-row">
                    <span className="wod-info-lbl">Due Date</span>
                    <span className="wod-info-val">{fmtDate(workOrder.Due_Date)}</span>
                  </div>
                </div>
              </div>

              {/* Entry Request */}
              {workOrder.Entry_Request_Status && (
                <div className="wod-info-card wod-card-full">
                  <div className="wod-info-card-hd">
                    <span className="wod-info-icon wod-info-icon-purple"><FaKey /></span>
                    <h3 className="wod-info-card-title">Entry Request</h3>
                  </div>
                  <div className="wod-entry-request-body">
                    <div className={`wod-entry-status-badge wod-er-${(workOrder.Entry_Request_Status || '').toLowerCase()}`}>
                      {workOrder.Entry_Request_Status === 'Pending'  && <FaClock style={{ marginRight: 5 }} />}
                      {workOrder.Entry_Request_Status === 'Approved' && <FaCheckCircle style={{ marginRight: 5 }} />}
                      {workOrder.Entry_Request_Status === 'Denied'   && <FaTimes style={{ marginRight: 5 }} />}
                      {workOrder.Entry_Request_Status}
                    </div>
                    {workOrder.Entry_Request_Status === 'Pending' && (
                      <p className="wod-er-pending-msg">Waiting for tenant to respond.</p>
                    )}
                    {workOrder.Entry_Request_Status === 'Approved' && (
                      <p className="wod-er-accepted-msg">Tenant approved entry access.</p>
                    )}
                    {workOrder.Entry_Request_Status === 'Denied' && (
                      <p className="wod-er-declined-msg">Tenant declined the entry request.</p>
                    )}
                    {workOrder.Entry_Request_Date && (
                      <div className="wod-er-date">
                        <FaCalendarAlt style={{ marginRight: 5, fontSize: 11 }} />
                        {fmtDate(workOrder.Entry_Request_Date)}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Assigned Vendors */}
              <div className="wod-info-card wod-card-full">
                <div className="wod-info-card-hd">
                  <span className="wod-info-icon wod-info-icon-orange"><FaTools /></span>
                  <h3 className="wod-info-card-title">Assigned Vendors</h3>
                  <button className="wod-vendor-inline-add" onClick={() => { setSelectedVendorId(''); setAssignNotes(''); setShowAddVendorModal(true); }}>
                    <FaPlus />
                  </button>
                </div>
                {woAssignedVendors.length === 0 ? (
                  <div className="wod-vacant-msg">
                    <FaTools className="wod-vacant-icon" />
                    <span>No vendors assigned</span>
                  </div>
                ) : (
                  <div className="wod-assigned-vendor-list">
                    {woAssignedVendors.map((v) => (
                      <div key={v.Assignment_id} className="wod-assigned-vendor-row">
                        <div className="wod-av-left">
                          <div className="wod-av-trade">{v.Trade || 'Service'}</div>
                          <div className="wod-av-name-row">
                            <div className="wod-av-name">{v.Full_Name || '—'}</div>
                            {v.Phone_Number && (
                              <div className="wod-av-phone"><FaPhone style={{ fontSize: 10, marginRight: 4 }} />{formatPhone(v.Phone_Number)}</div>
                            )}
                          </div>
                          {v.Notes && <div className="wod-av-notes">{v.Notes}</div>}
                        </div>
                        <button className="wod-av-remove" onClick={() => handleRemoveAssignedVendor(v.Assignment_id)} title="Remove">
                          <FaTimes />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Update Status */}
              <div className="wod-info-card wod-card-full">
                <div className="wod-info-card-hd">
                  <span className="wod-info-icon wod-info-icon-orange"><FaClock /></span>
                  <h3 className="wod-info-card-title">Update Status</h3>
                </div>
                <div className="wod-status-actions">
                  <button
                    className={`wod-action-btn wod-btn-inprog ${status === 'In Progress' ? 'wod-btn-active' : ''}`}
                    onClick={() => status !== 'In Progress' && handleUpdateStatus('In Progress')}
                    disabled={statusUpdating || status === 'In Progress' || status === 'Completed'}
                  >
                    <FaClock /> Mark In Progress
                  </button>
                  <button
                    className="wod-action-btn wod-btn-expense"
                    onClick={() => setShowExpenseModal(true)}
                  >
                    <FaDollarSign /> Attach Expense
                  </button>
                  <button
                    className={`wod-action-btn wod-btn-done ${status === 'Completed' ? 'wod-btn-active' : ''}`}
                    onClick={() => status !== 'Completed' && handleUpdateStatus('Completed')}
                    disabled={statusUpdating || status === 'Completed'}
                  >
                    <FaCheckCircle /> Mark Completed
                  </button>
                  {status === 'Completed' && (
                    <button
                      className="wod-action-btn wod-btn-reopen"
                      onClick={() => handleUpdateStatus('Open')}
                      disabled={statusUpdating}
                    >
                      <FaExclamationTriangle /> Reopen
                    </button>
                  )}
                </div>
              </div>

            </div>
          </div>

          {/* Attach Expense Modal */}
          {showExpenseModal && (() => {
            const cutoff = new Date();
            cutoff.setMonth(cutoff.getMonth() - 4);
            const propertyExpenses = allExpenses.filter(e => {
              // When door-scoped, allExpenses already contains only this unit's expenses
              if (!workOrder.Door_id && workOrder.Property_Name && e.property !== workOrder.Property_Name) return false;
              if (e.date) {
                const d = new Date(e.date);
                if (!isNaN(d) && d < cutoff) return false;
              }
              return true;
            });
            return (
              <div className="modal-overlay" onClick={() => setShowExpenseModal(false)}>
                <div className="wo-modal" onClick={e => e.stopPropagation()}>
                  <div className="wo-modal-header">
                    <div>
                      <h2>Attach Expense</h2>
                      <p className="wo-modal-subtitle">
                        {workOrder.Property_Name
                          ? `${workOrder.Property_Name} — last 4 months`
                          : 'Select an existing expense to link'}
                      </p>
                    </div>
                    <button className="modal-close-btn" onClick={() => setShowExpenseModal(false)}>
                      <FaTimes />
                    </button>
                  </div>
                  <div className="wo-modal-body">
                    {propertyExpenses.length === 0 ? (
                      <p style={{ color: '#9ca3af', fontSize: 13, textAlign: 'center', padding: '20px 0' }}>
                        {allExpenses.length === 0
                          ? 'No expenses found. Add expenses in the Activity page first.'
                          : `No expenses for ${workOrder.Property_Name || 'this property'} in the last 4 months.`}
                      </p>
                    ) : (
                      <div className="wo-form-group wo-full">
                        <label className="wo-field-label">Select Expense *</label>
                        <select
                          className="wo-field-input"
                          value={selectedExpenseId}
                          onChange={e => setSelectedExpenseId(e.target.value)}
                        >
                          <option value="">Choose an expense...</option>
                          {propertyExpenses.map((exp) => (
                            <option key={exp.expense_id} value={exp.expense_id}>
                              ${exp.amount.toFixed(2)}
                              {exp.category ? ` — ${exp.category}` : ''}
                              {exp.vendor ? ` (${exp.vendor})` : ''}
                              {exp.date ? ` · ${fmtDate(exp.date)}` : ''}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}
                  </div>
                  <div className="wo-modal-footer">
                    <button className="wo-cancel-btn" onClick={() => setShowExpenseModal(false)}>Cancel</button>
                    <button
                      className="wo-submit-btn"
                      onClick={handleAttachExpense}
                      disabled={!selectedExpenseId}
                      style={{ opacity: selectedExpenseId ? 1 : 0.5 }}
                    >
                      Attach
                    </button>
                  </div>
                </div>
              </div>
            );
          })()}

          {/* Edit Work Order Modal */}
          {showEditModal && (
            <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
              <div className="wo-modal" onClick={e => e.stopPropagation()}>
                <div className="wo-modal-header">
                  <div>
                    <h2>Edit Work Order</h2>
                    <p className="wo-modal-subtitle">Update the details below</p>
                  </div>
                  <button className="modal-close-btn" onClick={() => setShowEditModal(false)}><FaTimes /></button>
                </div>
                <div className="wo-modal-body">
                  <div className="wo-form-group wo-full">
                    <label className="wo-field-label">Title *</label>
                    <input
                      className="wo-field-input"
                      value={editForm.title}
                      onChange={e => setEditForm(p => ({ ...p, title: e.target.value }))}
                    />
                  </div>
                  <div className="wo-form-row">
                    <div className="wo-form-group">
                      <label className="wo-field-label">Priority</label>
                      <select
                        className="wo-field-input"
                        value={editForm.priority}
                        onChange={e => setEditForm(p => ({ ...p, priority: e.target.value }))}
                      >
                        <option value="Low">Low</option>
                        <option value="Medium">Medium</option>
                        <option value="Urgent">Urgent</option>
                      </select>
                    </div>
                    <div className="wo-form-group">
                      <label className="wo-field-label">Due Date</label>
                      <input
                        type="date"
                        className="wo-field-input"
                        value={editForm.dueDate}
                        onChange={e => setEditForm(p => ({ ...p, dueDate: e.target.value }))}
                      />
                    </div>
                  </div>
                  <div className="wo-form-group wo-full">
                    <label className="wo-field-label">Category *</label>
                    <select
                      className="wo-field-input"
                      value={editForm.category}
                      onChange={e => setEditForm(p => ({ ...p, category: e.target.value }))}
                    >
                      <option value="">Select category...</option>
                      {['Plumbing','Electrical','HVAC','Appliance','Structural','Carpentry','Painting','Roofing','Pest','Landscaping','Cleaning','General Maintenance','Other'].map(c => (
                        <option key={c} value={c}>{c}</option>
                      ))}
                    </select>
                  </div>
                  <div className="wo-form-group wo-full">
                    <label className="wo-field-label">Description</label>
                    <textarea
                      className="wo-field-textarea"
                      value={editForm.details}
                      onChange={e => setEditForm(p => ({ ...p, details: e.target.value }))}
                    />
                  </div>
                  {/* Photo upload */}
                  <div className="wo-form-group wo-full">
                    <label className="wo-field-label">Add Photos</label>
                    <div
                      className="wo-photo-upload-area"
                      onClick={() => editPhotoInputRef.current?.click()}
                    >
                      <FaImages className="wo-photo-upload-icon" />
                      <span className="wo-photo-upload-text">Click to add photos</span>
                    </div>
                    <input
                      ref={editPhotoInputRef}
                      type="file"
                      accept="image/*"
                      multiple
                      style={{ display: 'none' }}
                      onChange={e => {
                        const files = Array.from(e.target.files || []);
                        setEditPhotos(prev => [...prev, ...files]);
                        e.target.value = '';
                      }}
                    />
                    {editPhotos.length > 0 && (
                      <div className="wo-photo-preview-row">
                        {editPhotos.map((file, i) => (
                          <div key={i} className="wo-photo-preview-wrap">
                            <img src={URL.createObjectURL(file)} alt="" className="wo-photo-preview-img" />
                            <button
                              className="wo-photo-preview-remove"
                              onClick={() => setEditPhotos(prev => prev.filter((_, j) => j !== i))}
                            >×</button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="wo-form-group wo-full">
                    <div className="wo-entry-request-card">
                      <div className="wo-entry-request-left">
                        <FaKey className="wo-entry-request-icon" />
                        <div>
                          <div className="wo-entry-request-title">Request entry if tenant isn't home</div>
                          <div className="wo-entry-request-sub">Allows a technician to access the unit without the tenant present.</div>
                        </div>
                      </div>
                      <label className="wo-er-toggle">
                        <input
                          type="checkbox"
                          checked={editForm.entryRequest}
                          onChange={e => setEditForm(p => ({ ...p, entryRequest: e.target.checked, entryDate: e.target.checked ? p.entryDate : '' }))}
                        />
                        <span className="wo-er-toggle-track" />
                      </label>
                    </div>
                    {editForm.entryRequest && (
                      <div style={{ marginTop: 10 }}>
                        <label className="wo-field-label">Requested Entry Date *</label>
                        <input
                          type="date"
                          className="wo-field-input"
                          value={editForm.entryDate}
                          min={new Date().toISOString().split('T')[0]}
                          onChange={e => setEditForm(p => ({ ...p, entryDate: e.target.value }))}
                        />
                      </div>
                    )}
                  </div>
                </div>
                <div className="wo-modal-footer">
                  <button className="wo-cancel-btn" onClick={() => { setShowEditModal(false); setShowDeleteConfirm(true); }} style={{ color: '#dc2626', borderColor: '#dc2626' }}>
                    <FaTrash style={{ marginRight: 6 }} /> Delete
                  </button>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button className="wo-cancel-btn" onClick={() => setShowEditModal(false)}>Cancel</button>
                    <button
                      className="wo-submit-btn"
                      onClick={handleEditWorkOrder}
                      disabled={!editForm.title.trim()}
                      style={{ opacity: editForm.title.trim() ? 1 : 0.5 }}
                    >
                      Save Changes
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Assign Vendor Modal */}
          {showAddVendorModal && (
            <div className="modal-overlay" onClick={() => setShowAddVendorModal(false)}>
              <div className="wo-modal" style={{ maxWidth: 440 }} onClick={e => e.stopPropagation()}>
                <div className="wo-modal-header">
                  <div>
                    <h2>Assign Vendor</h2>
                    <p className="wo-modal-subtitle">Pick from your vendor pool</p>
                  </div>
                  <button className="modal-close-btn" onClick={() => setShowAddVendorModal(false)}><FaTimes /></button>
                </div>
                <div className="wo-modal-body">
                  {vendors.length === 0 ? (
                    <p style={{ color: '#9ca3af', fontSize: 13, textAlign: 'center', padding: '20px 0' }}>
                      No vendors in your pool yet. Add vendors from the Work Orders page.
                    </p>
                  ) : (
                    <div className="wod-vendor-pick-list">
                      {vendors.map((v) => {
                        const vid = v.id || v.ID;
                        const isSelected = String(selectedVendorId) === String(vid);
                        return (
                          <div
                            key={vid}
                            className={`wod-vendor-pick-row ${isSelected ? 'wod-vendor-pick-selected' : ''}`}
                            onClick={() => setSelectedVendorId(isSelected ? '' : vid)}
                          >
                            <div className="wod-vendor-pick-check">{isSelected ? '✓' : ''}</div>
                            <div className="wod-vendor-pick-info">
                              <div className="wod-vendor-pick-trade">{v.Trade || v.trade || '—'}</div>
                              <div className="wod-vendor-pick-name">{v.Full_Name || v.full_name}</div>
                              {(v.Phone_Number || v.phone_number) && (
                                <div className="wod-vendor-pick-phone">
                                  <FaPhone style={{ fontSize: 10, marginRight: 4 }} />{formatPhone(v.Phone_Number || v.phone_number)}
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  {vendors.length > 0 && (
                    <div className="wo-form-group wo-full" style={{ marginTop: 12 }}>
                      <label className="wo-field-label">Notes (optional)</label>
                      <textarea
                        className="wo-field-textarea"
                        placeholder="Instructions for this vendor…"
                        value={assignNotes}
                        onChange={e => setAssignNotes(e.target.value)}
                      />
                    </div>
                  )}
                </div>
                <div className="wo-modal-footer">
                  <button className="wo-cancel-btn" onClick={() => setShowAddVendorModal(false)}>Cancel</button>
                  <button
                    className="wo-submit-btn"
                    onClick={handleAssignVendor}
                    disabled={assignSubmitting || !selectedVendorId}
                    style={{ opacity: selectedVendorId ? 1 : 0.5 }}
                  >
                    {assignSubmitting ? 'Assigning…' : 'Assign Vendor'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Delete Confirmation Modal */}
          {showDeleteConfirm && (
            <div className="modal-overlay" onClick={() => setShowDeleteConfirm(false)}>
              <div className="wo-modal wod-delete-modal" onClick={e => e.stopPropagation()}>
                <div className="wo-modal-header">
                  <div>
                    <h2>Delete Work Order</h2>
                    <p className="wo-modal-subtitle">This action cannot be undone</p>
                  </div>
                  <button className="modal-close-btn" onClick={() => setShowDeleteConfirm(false)}>
                    <FaTimes />
                  </button>
                </div>
                <div className="wo-modal-body">
                  <div className="wod-delete-warning">
                    <FaTrash className="wod-delete-warning-icon" />
                    <p>Are you sure you want to delete <strong>"{workOrder.Title}"</strong>? All timeline events and attached data will be removed.</p>
                  </div>
                </div>
                <div className="wo-modal-footer">
                  <button className="wo-cancel-btn" onClick={() => setShowDeleteConfirm(false)}>Cancel</button>
                  <button className="wod-confirm-delete-btn" onClick={handleDeleteWorkOrder}>
                    <FaTrash /> Delete Work Order
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Photo Lightbox */}
          {lightboxPhoto && (
            <div className="wod-lightbox" onClick={() => setLightboxPhoto(null)}>
              <button className="wod-lightbox-close" onClick={() => setLightboxPhoto(null)}><FaTimes /></button>
              <img src={lightboxPhoto.blobUrl} alt="Work order" className="wod-lightbox-img" onClick={e => e.stopPropagation()} />
            </div>
          )}

        </div>
      </div>
    </div>
  );
}

export default WorkOrderDetails;
